<template>
  <Modal show class="shop-sets-dialog-modal" v-on="$listeners">
    <Card theme="flat" class="shop-sets-dialog">
      <template #header>
        <MediaComponent v-model="data.image" placement="inside" size="cover" :description="false" />
      </template>
      <template #default>
        <InputComponent
          v-model="data.name"
          :label="labels.name"
          :is-valid="validator.fields.name.valid"
          :validation-message="validator.fields.name.error"
          required
        />
        <InputComponent
          :value="data.description"
          :label="labels.description"
          :is-valid="validator.fields.description.valid"
          :validation-message="validator.fields.description.error"
        >
          <RichTextComponent v-model="data.description" />
        </InputComponent>
        <InputComponent :label="labels.product">
          <ProductSelect
            v-model="products"
            :modal-wrapper="Portal"
            :items="availableProducts"
            multiple
            :show-filters="false"
            class="shop-sets-dialog__product-select"
          >
            <template #empty>
              <div class="fade">{{ $t('selectx', { x: $tc('product', 2).toLowerCase() }) }}...</div>
            </template>
            <template #subtitle="{ item }">
              <span>{{ item.meta.category.label }} - {{ item.meta.subcategory.label }}</span>
            </template>
          </ProductSelect>
        </InputComponent>
      </template>
      <template #footer>
        <ButtonComponent :icon="['fal', 'times']" :label="$t('close')" @click="$emit('close')" />
        <ButtonComponent
          v-if="!isNew"
          theme="error"
          :icon="['fal', 'trash']"
          :label="$t('delete')"
          confirm
          @click="() => $emit('remove', value) && $emit('close')"
        />
        <ButtonComponent
          :label="$t(isNew ? 'create' : 'update')"
          :icon="['fal', 'arrow-to-right']"
          theme="success"
          type="button"
          style="margin-left: auto"
          @click="submit"
          :disabled="!validator.passes"
        />
      </template>
    </Card>
  </Modal>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import ModelMixin from '@/mixins/Model'
import Model from '@/models/Shop/set'
import { mapi18n } from '@/i18n'
import { Slugify } from '@/utils'

import RichTextComponent from '@kvass/rich-text'
import { InputComponent } from 'vue-elder-input'
import { MediaComponent } from '@kvass/media'
import { ModalComponent as Modal } from 'vue-elder-modal'
import ProductSelect from '@/components/Product/Select/Modal'
import Card from '@/components/Card'
import Portal from '@/components/Portal'

export default {
  mixins: [
    Validator({
      data: 'data',
      labels: 'labels',
      rules: {
        name: 'required|string',
        description: 'string',
      },
    }),
    ModelMixin({
      source: 'value',
      model: Model,
      onUpdate() {},
    }),
  ],
  props: {
    value: [Object, Boolean],
    shop: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selected: null,
      products: [],
      Portal,
    }
  },
  computed: {
    ...mapi18n('labels', ['name', 'description', ['image', 1], { product: 'productsThatArePartOfSet' }]),
    isNew() {
      return typeof this.value === 'boolean'
    },
    availableProducts() {
      return this.shop.categories.reduce((productLinks, category) => {
        category.categories.forEach(subcategory => {
          subcategory.items.forEach(link =>
            productLinks.push({
              ...link.product,
              meta: {
                category,
                subcategory,
                link,
              },
            }),
          )
        })

        return productLinks
      }, [])
    },
    currentlySelectedProducts() {
      return this.availableProducts.filter(p => (p.meta.link.sets || []).includes(this.value.id))
    },
  },
  methods: {
    submit() {
      let currentlySelectedProductsIds = this.currentlySelectedProducts.map(x => x.id)
      let productsIds = this.products.map(x => x.id)

      let newProducts = this.products.filter(p => !currentlySelectedProductsIds.includes(p.id))
      let removedProducts = this.currentlySelectedProducts.filter(p => !productsIds.includes(p.id))

      let data = this.getData()
      if (!data.id) data.id = Slugify(data.name)

      this.$emit('input', data)
      this.$emit('products:add', {
        ids: newProducts.map(p => p.meta.link.id),
        set: data,
      })
      this.$emit('products:remove', {
        ids: removedProducts.map(p => p.meta.link.id),
        set: data,
      })
    },
  },
  created() {
    this.products = [...this.currentlySelectedProducts]
  },
  components: {
    Modal,
    Card,
    RichTextComponent,
    InputComponent,
    ProductSelect,
    MediaComponent,
  },
}
</script>

<style lang="scss">
.shop-sets-dialog {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;

  & > .card__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .product-select-modal > .elder-modal > .card {
    max-width: 600px;
  }

  &__product-select {
    padding: 1.1em;
  }
  .kvass-media-droparea {
    min-width: 250px;
    height: 200px;
  }
}
</style>
